<template>
  <div>
    <div class="flex items-center justify-between mb-6 max-md:flex-col max-md:items-start max-md:gap-3">
      <h1 class="text-[26px] font-bold tracking-tight text-ink m-0 max-md:text-[22px]">Deadlines</h1>
      <div class="flex items-center gap-2">
        <button class="cal-nav-btn" @click="prevMonth">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
        </button>
        <span class="text-sm font-semibold text-ink min-w-[140px] text-center">{{ monthLabel }}</span>
        <button class="cal-nav-btn" @click="nextMonth">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
        </button>
        <button class="cal-today-btn" @click="goToday">Today</button>
      </div>
    </div>

    <skeleton-deadlines v-if="isLoading"></skeleton-deadlines>

    <!-- Desktop: month grid -->
    <div v-if="!isLoading" class="cal-grid max-md:hidden">
      <div class="cal-header" v-for="day in weekDays" :key="day">{{ day }}</div>
      <div
        v-for="(cell, i) in calendarCells"
        :key="i"
        class="cal-cell"
        :class="{
          'cal-cell--outside': !cell.currentMonth,
          'cal-cell--today': cell.isToday
        }">
        <span class="cal-day-num">{{ cell.day }}</span>
        <div v-if="cell.items.length" class="cal-items">
          <router-link
            v-for="(item, j) in cell.items.slice(0, 3)"
            :key="j"
            :to="{ name: 'admin-applicant', params: { id: item.applicantId } }"
            class="cal-pill"
            :class="pillClass(item)"
            :title="item.label">
            {{ item.label }}
          </router-link>
          <span v-if="cell.items.length > 3" class="text-[10px] text-muted font-medium mt-0.5">+{{ cell.items.length - 3 }} more</span>
        </div>
      </div>
    </div>

    <!-- Mobile: list view grouped by week -->
    <div v-if="!isLoading" class="hidden max-md:block">
      <div v-for="(week, wi) in mobileWeeks" :key="wi" class="mb-4">
        <div class="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted mb-2 px-1">{{ week.label }}</div>
        <div v-if="week.items.length === 0" class="text-[13px] text-placeholder px-1 py-2">No deadlines this week</div>
        <div v-else class="flex flex-col gap-1">
          <router-link
            v-for="(item, j) in week.items"
            :key="j"
            :to="{ name: 'admin-applicant', params: { id: item.applicantId } }"
            class="flex items-center gap-3 px-3.5 py-3 bg-white border border-black/6 rounded-[16px] no-underline transition-colors duration-150 hover:bg-canvas hover:no-underline">
            <span class="w-2 h-2 rounded-full shrink-0" :class="dotClass(item)"></span>
            <div class="flex-1 min-w-0">
              <span class="text-sm text-ink font-medium truncate block">{{ item.label }}</span>
              <span class="text-xs text-muted">{{ formatDate(item.date) }}</span>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import dayjs from 'dayjs'
import SkeletonDeadlines from '../UIComponents/SkeletonDeadlines'

export default {
  name: 'admin-deadlines',
  components: { SkeletonDeadlines },
  data () {
    return {
      currentMonth: dayjs().startOf('month'),
      isLoading: false
    }
  },
  computed: {
    ...mapState({
      applicants: state => state.applicants.applicants
    }),
    weekDays () {
      return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    },
    monthLabel () {
      return this.currentMonth.format('MMMM YYYY')
    },
    allDeadlines () {
      if (!this.applicants) return []
      const items = []
      this.applicants.forEach(a => {
        if (!a.isActive) return
        const name = (a.firstName || '') + ' ' + (a.lastName || '')
        // Main deadline
        if (a.deadline) {
          items.push({
            date: a.deadline,
            label: name.trim(),
            applicantId: a.id,
            completed: false,
            type: 'deadline'
          })
        }
        // Key dates
        if (a.keyDates && a.keyDates.length) {
          a.keyDates.forEach(kd => {
            if (kd.date) {
              items.push({
                date: kd.date,
                label: name.trim() + ' — ' + kd.label,
                applicantId: a.id,
                completed: !!kd.completed,
                type: 'keyDate'
              })
            }
          })
        }
      })
      return items
    },
    calendarCells () {
      const start = this.currentMonth.startOf('month')
      const end = this.currentMonth.endOf('month')
      // Monday = 1, Sunday = 0 in dayjs; we need Monday-start weeks
      let startDay = start.day() // 0=Sun
      startDay = startDay === 0 ? 6 : startDay - 1 // convert to Mon=0
      const daysInMonth = end.date()
      const cells = []
      const today = dayjs().format('YYYY-MM-DD')

      // Previous month padding
      const prevMonth = start.subtract(1, 'month')
      const prevDays = prevMonth.daysInMonth()
      for (let i = startDay - 1; i >= 0; i--) {
        const d = prevDays - i
        const dateStr = prevMonth.date(d).format('YYYY-MM-DD')
        cells.push({ day: d, currentMonth: false, isToday: dateStr === today, items: this.getItemsForDate(dateStr) })
      }

      // Current month
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = start.date(d).format('YYYY-MM-DD')
        cells.push({ day: d, currentMonth: true, isToday: dateStr === today, items: this.getItemsForDate(dateStr) })
      }

      // Next month padding to fill 6 rows
      const remaining = (7 - (cells.length % 7)) % 7
      const nextMonth = start.add(1, 'month')
      for (let d = 1; d <= remaining; d++) {
        const dateStr = nextMonth.date(d).format('YYYY-MM-DD')
        cells.push({ day: d, currentMonth: false, isToday: dateStr === today, items: this.getItemsForDate(dateStr) })
      }

      return cells
    },
    mobileWeeks () {
      const weeks = []
      const start = this.currentMonth.startOf('month')
      const end = this.currentMonth.endOf('month')
      let weekStart = start.startOf('week').add(start.day() === 0 ? -6 : 1, 'day')
      // Adjust: dayjs startOf('week') is Sunday-based, we want Monday
      if (start.day() !== 1) {
        weekStart = start.day() === 0 ? start.subtract(6, 'day') : start.subtract(start.day() - 1, 'day')
      }

      let cursor = weekStart
      while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
        const wEnd = cursor.add(6, 'day')
        const label = cursor.format('MMM D') + ' – ' + wEnd.format('MMM D')
        const items = []
        for (let d = 0; d < 7; d++) {
          const dateStr = cursor.add(d, 'day').format('YYYY-MM-DD')
          items.push(...this.getItemsForDate(dateStr))
        }
        // Sort items by date
        items.sort((a, b) => (a.date > b.date ? 1 : -1))
        weeks.push({ label, items })
        cursor = cursor.add(7, 'day')
      }
      return weeks
    }
  },
  methods: {
    prevMonth () {
      this.currentMonth = this.currentMonth.subtract(1, 'month')
    },
    nextMonth () {
      this.currentMonth = this.currentMonth.add(1, 'month')
    },
    goToday () {
      this.currentMonth = dayjs().startOf('month')
    },
    getItemsForDate (dateStr) {
      return this.allDeadlines.filter(item => {
        const d = dayjs(item.date)
        return d.isValid() && d.format('YYYY-MM-DD') === dateStr
      })
    },
    getDeadlineStatus (item) {
      if (item.completed) return 'completed'
      const today = dayjs().startOf('day')
      const date = dayjs(item.date)
      if (date.isBefore(today)) return 'overdue'
      if (date.diff(today, 'day') <= 14) return 'soon'
      return 'normal'
    },
    pillClass (item) {
      const status = this.getDeadlineStatus(item)
      return {
        'cal-pill--overdue': status === 'overdue',
        'cal-pill--soon': status === 'soon',
        'cal-pill--normal': status === 'normal',
        'cal-pill--completed': status === 'completed'
      }
    },
    dotClass (item) {
      const status = this.getDeadlineStatus(item)
      return {
        'bg-[#e53935]': status === 'overdue',
        'bg-[#f57c00]': status === 'soon',
        'bg-primary': status === 'normal',
        'bg-[#b5b0b8]': status === 'completed'
      }
    },
    formatDate (date) {
      return dayjs(date).format('ddd, MMM D')
    }
  },
  created () {
    if (!this.applicants || this.applicants.length === 0) {
      this.isLoading = true
      this.$store.dispatch('GET_APPLICANTS').then(() => {
        this.isLoading = false
      }).catch(() => {
        this.isLoading = false
      })
    }
  }
}
</script>

<style scoped>
.cal-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  color: #131415;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.cal-nav-btn:hover {
  border-color: rgba(0, 0, 0, 0.12);
  background: #f4f4f8;
}

.cal-today-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  padding: 0 16px;
  margin-left: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  color: #131415;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.cal-today-btn:hover {
  border-color: rgba(0, 0, 0, 0.12);
  background: #f4f4f8;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.cal-header {
  padding: 10px 8px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7D7480;
  background: #f6f6fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.cal-cell {
  min-height: 90px;
  padding: 6px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  border-right: 1px solid rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
}

.cal-cell:nth-child(7n + 14) {
  border-right: none;
}

.cal-cell--outside {
  background: #f8f8fb;
}

.cal-cell--outside .cal-day-num {
  color: #c0bcc4;
}

.cal-cell--today {
  background: rgba(124, 91, 234, 0.03);
}

.cal-cell--today .cal-day-num {
  background: #7c5bea;
  color: #fff;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cal-day-num {
  font-size: 12px;
  font-weight: 600;
  color: #131415;
  margin-bottom: 4px;
}

.cal-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.cal-pill {
  display: block;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 6px;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.15s;
  line-height: 1.4;
}

.cal-pill:hover {
  opacity: 0.8;
  text-decoration: none;
}

.cal-pill--overdue {
  background: rgba(229, 57, 53, 0.1);
  color: #e53935;
}

.cal-pill--soon {
  background: rgba(245, 124, 0, 0.1);
  color: #f57c00;
}

.cal-pill--normal {
  background: rgba(124, 91, 234, 0.08);
  color: #7c5bea;
}

.cal-pill--completed {
  background: rgba(0, 0, 0, 0.04);
  color: #b5b0b8;
  text-decoration: line-through;
}
</style>
